import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { BaseModal } from '@/components/shared';
import type { ColaboradorPendente } from '../../../models';
import type { PreCadastroPendente } from '../../../services/preCadastroAdmissaoService';
import { usePreCadastrosPendentesQuery, useVincularPreCadastroManual } from '../../../hooks/data';

interface VincularPreCadastroModalProps {
  solicitacaoId: string;
  colaboradorPendente?: ColaboradorPendente | null;
  /** Nome/e-mail da Admissão (pendente ou já vinculada) — pra comparar na confirmação. */
  nomeSolicitacao: string;
  emailSolicitacao: string;
  isOpen: boolean;
  onClose: () => void;
}

const ORIGEM_LABELS: Record<PreCadastroPendente['origem'], string> = {
  recrutamento: 'Recrutamento',
  sharepoint: 'Forms antigo',
};

function normalizar(texto: string | null | undefined): string {
  return (texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Vínculo manual de pré-cadastro — quando o automático (pelo e-mail pessoal) não achou o
 * formulário da pessoa. Mesmo fluxo em 2 passos do "Vincular Colaborador": escolher e
 * confirmar comparando com o que foi informado na abertura (não dá pra desfazer pela UI).
 */
export default function VincularPreCadastroModal({
  solicitacaoId,
  colaboradorPendente,
  nomeSolicitacao,
  emailSolicitacao,
  isOpen,
  onClose,
}: VincularPreCadastroModalProps) {
  const { data: pendentes = [], isLoading, isError } = usePreCadastrosPendentesQuery(isOpen);
  const vincular = useVincularPreCadastroManual(solicitacaoId);
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<PreCadastroPendente | null>(null);
  const [etapaConfirmacao, setEtapaConfirmacao] = useState(false);

  const filtrados = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return pendentes;
    return pendentes.filter((p) => normalizar(p.nome).includes(termo) || normalizar(p.emailPessoal).includes(termo));
  }, [busca, pendentes]);

  const handleClose = () => {
    setBusca('');
    setSelecionado(null);
    setEtapaConfirmacao(false);
    onClose();
  };

  const handleConfirmar = async () => {
    if (!selecionado) return;
    await vincular.mutateAsync(selecionado.id);
    handleClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Vincular pré-cadastro" maxWidth="max-w-lg">
      {!etapaConfirmacao ? (
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Formulários de admissão que ainda não foram vinculados a nenhuma Admissão. Busque pelo nome ou pelo e-mail
            pessoal que a pessoa usou no formulário.
          </p>
          <input
            className="input-primary w-full"
            placeholder="Buscar por nome ou e-mail..."
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            autoFocus
          />
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {isLoading && <div className="h-24 animate-pulse rounded-md bg-gray-100" />}
            {isError && <p className="text-sm text-red-600">Não foi possível carregar os pré-cadastros.</p>}
            {!isLoading && !isError && filtrados.length === 0 && (
              <p className="py-6 text-center text-sm text-gray-500">
                {pendentes.length === 0 ? 'Nenhum pré-cadastro pendente.' : 'Nenhum pré-cadastro encontrado com essa busca.'}
              </p>
            )}
            {filtrados.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelecionado(p)}
                className={clsx(
                  'w-full rounded-md border px-3 py-2 text-left text-sm',
                  selecionado?.id === p.id ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                )}
              >
                <p className="font-medium text-gray-900">{p.nome ?? 'Sem nome no formulário'}</p>
                <p className="text-xs text-gray-600">
                  {[
                    p.emailPessoal,
                    ORIGEM_LABELS[p.origem] ?? p.origem,
                    p.criadoEm ? new Date(p.criadoEm).toLocaleDateString('pt-BR') : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={handleClose}>
              Cancelar
            </button>
            <button type="button" className="btn-primary" disabled={!selecionado} onClick={() => setEtapaConfirmacao(true)}>
              Continuar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Confirme que o formulário é da mesma pessoa desta Admissão. Esta ação não pode ser desfeita pela tela.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs font-medium uppercase text-gray-400">Admissão</p>
              <p className="mt-1 font-medium text-gray-900">{colaboradorPendente?.nome ?? nomeSolicitacao}</p>
              <p className="break-all text-gray-600">{colaboradorPendente?.emailInformado ?? emailSolicitacao}</p>
            </div>
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3">
              <p className="text-xs font-medium uppercase text-indigo-500">Pré-cadastro</p>
              <p className="mt-1 font-medium text-gray-900">{selecionado?.nome ?? 'Sem nome no formulário'}</p>
              <p className="break-all text-gray-600">{selecionado?.emailPessoal ?? '—'}</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={() => setEtapaConfirmacao(false)}>
              Voltar
            </button>
            <button type="button" className="btn-primary" disabled={vincular.isPending} onClick={handleConfirmar}>
              {vincular.isPending ? 'Vinculando...' : 'Confirmar vínculo'}
            </button>
          </div>
        </div>
      )}
    </BaseModal>
  );
}
